import React, { useContext, useState } from 'react'
import { Trans, t } from '@lingui/macro'
import IconDoc from '../../../../components/common/icon/IconDoc'
import { Modal } from '../../../../components/common/modal/Modal'
import Form, { FormRow } from '../../../../components/common/form/Form'
import HorizontalChoiceSelector from '../../../../components/common/horizontal-choice-selector/HorizontalChoiceSelector'
import IconMap from '../../../../components/common/icon/IconMap'
import IconEnvelope from '../../../../components/common/icon/IconEnvelope'
import IconScreenDesktop from '../../../../components/common/icon/IconScreenDesktop'
import IconBubble from '../../../../components/common/icon/IconBubble'
import { linkBuilder } from '../../../../../../shared/linkBuilder'
import { navigateTo } from '../../../../app/util/appHistory'
import CampaignSurveyShareActionModal from '../../campaign-survey-share-action-modal/CampaignSurveyShareActionModal'
import CampaignTopicShareActionModal from '../../campaign-topic-share-action-modal/CampaignTopicShareActionModal'
import CampaignPathwayShareActionModal from '../../campaign-pathway-share-action-modal/CampaignPathwayShareActionModal'

import { ManageCampaignContext } from '../../ManageCampaignPage'
import { isCampaignImmediate } from '../../utils'
import CampaignCertificationShareActionModal from '../../campaign-certification-share-action-modal/CampaignCertificationShareActionModal'
import IconBadge from '../../../../components/common/icon/IconBadge'
import IconPeople from 'front-end/src/components/common/icon/IconPeople'
import { useFeatureFlags } from 'front-end/src/app/hooks/useFeatureFlags'
import './CreateCampaignActionModal.less'
import { ButtonLight } from 'front-end/src/components'
import { CampaignActionTypeEnum } from 'shared/enums/campaigns'

export interface ICreateCampaignActionModalProps {
  show?: boolean
  onHide: () => void
  onCreate: () => void
}

export const translateActionType = (actionType: CampaignActionTypeEnum) => {
  return {
    [CampaignActionTypeEnum.TOPIC_SHARE_ACTION]: t`Share resources`,
    [CampaignActionTypeEnum.PATHWAY_SHARE_ACTION]: t`Share pathways`,
    [CampaignActionTypeEnum.CERTIFICATION_SHARE_ACTION]: t`Share certifications`,
    [CampaignActionTypeEnum.EMAIL]: t`Send email`,
    [CampaignActionTypeEnum.MANAGER_NOTIFICATION]: t`Send manager notification`,
    [CampaignActionTypeEnum.BANNER]: t`Show a banner`,
    [CampaignActionTypeEnum.SURVEY_SHARE_ACTION]: t`Share user surveys`,
    [CampaignActionTypeEnum.MESSAGE]: t`Send message`
  }[actionType]
}

export const CreateCampaignActionModal = (
  props: ICreateCampaignActionModalProps
) => {
  const { campaign } = useContext(ManageCampaignContext)
  const [showTopicPickModal, setShowTopicPickModal] = useState<boolean>(false)
  const [showSurveyPickModal, setShowSurveyPickModal] = useState<boolean>(false)
  const [showCertificationPickModal, setShowCertificationPickModal] =
    useState<boolean>(false)
  const [showPathwayPickModal, setShowPathwayPickModal] =
    useState<boolean>(false)
  const { featureFlags } = useFeatureFlags()

  let availableActionButtons = [...allActionButtons]
  if (!featureFlags?.Slack && !featureFlags?.sms && !featureFlags?.msteams) {
    availableActionButtons = availableActionButtons.filter(
      (actionButton) => actionButton.value !== CampaignActionTypeEnum.MESSAGE
    )
  }

  if (!featureFlags?.campaign_manager_notification) {
    availableActionButtons = availableActionButtons.filter(
      (actionButton) =>
        actionButton.value !== CampaignActionTypeEnum.MANAGER_NOTIFICATION
    )
  }

  if (isCampaignImmediate(campaign)) {
    availableActionButtons = availableActionButtons.filter(
      (actionButton) => actionButton.value !== CampaignActionTypeEnum.BANNER
    )
  }

  const onActionTypeChanged = (actionType) => {
    if (actionType === CampaignActionTypeEnum.PATHWAY_SHARE_ACTION) {
      // Additional modal on top of this one is shown
      setShowPathwayPickModal(true)
      return
    }
    if (actionType === CampaignActionTypeEnum.CERTIFICATION_SHARE_ACTION) {
      // Additional modal on top of this one is shown
      setShowCertificationPickModal(true)
      return
    }
    if (actionType === CampaignActionTypeEnum.TOPIC_SHARE_ACTION) {
      // Additional modal on top of this one is shown
      setShowTopicPickModal(true)
      return
    }
    if (actionType === CampaignActionTypeEnum.SURVEY_SHARE_ACTION) {
      // Additional modal on top of this one is shown
      setShowSurveyPickModal(true)
      return
    }
    if (actionType === CampaignActionTypeEnum.EMAIL) {
      navigateTo(linkBuilder.campaignNewEmail(campaign.id))
    }

    if (actionType === CampaignActionTypeEnum.BANNER) {
      navigateTo(linkBuilder.campaignNewBanner(campaign.id))
    }

    if (actionType === CampaignActionTypeEnum.MESSAGE) {
      navigateTo(linkBuilder.campaignNewMessage(campaign.id))
    }
    if (actionType === CampaignActionTypeEnum.MANAGER_NOTIFICATION) {
      navigateTo(linkBuilder.campaignNewManagerNotification(campaign.id))
    }

    props.onCreate()
  }

  return (
    <>
      <Modal
        className="CreateCampaignActionModal"
        header={t`Select an action type`}
        show={props.show}
        onHide={props.onHide}
        controls={[
          <ButtonLight key={1} onClick={props.onHide}>
            <Trans>Cancel</Trans>
          </ButtonLight>
        ]}
      >
        <Form>
          <FormRow>
            <HorizontalChoiceSelector
              colorScheme="light-gray"
              name="type"
              onChange={(val) => {
                onActionTypeChanged(val)
              }}
              options={availableActionButtons}
            />
          </FormRow>
        </Form>
      </Modal>

      {showTopicPickModal && (
        <CampaignTopicShareActionModal
          onCancel={() => {
            props.onHide()
          }}
          onHide={() => {
            props.onHide()
          }}
          campaignId={campaign.id}
        />
      )}

      {showPathwayPickModal && (
        <CampaignPathwayShareActionModal
          onCancel={() => {
            props.onHide()
          }}
          onHide={() => {
            props.onHide()
          }}
          campaignId={campaign.id}
        />
      )}

      {showCertificationPickModal && (
        <CampaignCertificationShareActionModal
          onCancel={() => {
            props.onHide()
          }}
          onHide={() => {
            props.onHide()
          }}
          campaignId={campaign.id}
        />
      )}

      {showSurveyPickModal && (
        <CampaignSurveyShareActionModal
          onCancel={() => {
            props.onHide()
          }}
          onHide={() => {
            props.onHide()
          }}
          campaignId={campaign.id}
        />
      )}
    </>
  )
}

const allActionButtons = [
  {
    value: CampaignActionTypeEnum.BANNER,
    header: (
      <>
        <IconScreenDesktop />
        <Trans>Show banner</Trans>
      </>
    ),
    description: (
      <div className="ChoiceDescription">
        <Trans>Display a banner on persons homepage</Trans>
      </div>
    )
  },
  {
    value: CampaignActionTypeEnum.EMAIL,
    header: (
      <>
        <IconEnvelope />
        <Trans>Send email</Trans>
      </>
    ),
    description: (
      <div className="ChoiceDescription">
        <Trans>Send a message right into the inbox</Trans>
      </div>
    )
  },
  {
    value: CampaignActionTypeEnum.MESSAGE,
    header: (
      <>
        <IconBubble />
        <Trans>Send message</Trans>
      </>
    ),
    description: (
      <div className="ChoiceDescription">
        <Trans>Send people a direct message</Trans>
      </div>
    )
  },
  {
    value: CampaignActionTypeEnum.MANAGER_NOTIFICATION,
    header: (
      <>
        <IconPeople />
        <Trans>Notify manager</Trans>
      </>
    ),
    description: (
      <span className="ChoiceDescription">
        <Trans>Send a message to persons manager</Trans>
      </span>
    )
  },
  {
    value: CampaignActionTypeEnum.TOPIC_SHARE_ACTION,
    header: (
      <>
        <IconDoc />
        <Trans>Share resource</Trans>
      </>
    ),
    description: (
      <div className="ChoiceDescription">
        <Trans>Send people the resources they need</Trans>
      </div>
    )
  },
  {
    value: CampaignActionTypeEnum.PATHWAY_SHARE_ACTION,
    header: (
      <>
        <IconMap />
        <Trans>Share pathway</Trans>
      </>
    ),
    description: (
      <div className="ChoiceDescription">
        <Trans>Send people a pathway to complete</Trans>
      </div>
    )
  },
  {
    value: CampaignActionTypeEnum.SURVEY_SHARE_ACTION,
    header: (
      <>
        <i className="Icon icon icon-bubbles" />
        <Trans>Share survey</Trans>
      </>
    ),
    description: (
      <div className="ChoiceDescription">
        <Trans>Send people a survey to complete</Trans>
      </div>
    )
  },

  {
    value: CampaignActionTypeEnum.CERTIFICATION_SHARE_ACTION,
    header: (
      <>
        <IconBadge />
        <Trans>Share certification</Trans>
      </>
    ),
    description: (
      <div className="ChoiceDescription">
        <Trans>Send people a certification to complete</Trans>
      </div>
    )
  }
]
